/**
 * Catalog-driven source picker (DATA_VISUALIZATION_FRONTEND_PLAN §2.1).
 *
 * Nothing here is hard-coded: the options, their names, their help text and
 * their sections all come from `GET /data/sources`. Sections follow the
 * catalog's `group`, which is one per physical meter — the hub has three, which
 * is why six hub entries appear where an operator expects four. Dairy-scoped
 * roles expand to a per-facility checklist. Selected sources render as
 * removable pills above the trigger.
 *
 * Labels are the backend's `label`, never derived from the role key. Deriving
 * them is how the XRC2 plant meter came to be labelled "PGE Outlet Volume".
 */
import {
  Accordion,
  Badge,
  Button,
  Checkbox,
  Divider,
  Group,
  Pill,
  Popover,
  ScrollArea,
  Stack,
  Text,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import type { SourceCatalogEntry, SourcesCatalogResponse } from "../../api/types";
import {
  type Selection,
  hasSelection,
  selectionKey,
  selectionLabel,
  toggleSelection,
} from "./selection";
import { unitLabel } from "./units";

type Props = {
  catalog: SourcesCatalogResponse | undefined;
  value: Selection[];
  onChange: (next: Selection[]) => void;
  loading?: boolean;
};

export function SourcePicker({ catalog, value, onChange, loading }: Props) {
  const [opened, setOpened] = useState(false);

  const dairyFacilities = catalog?.facilities.filter((f) => f.scope === "dairy") ?? [];
  // Group in catalog order, so the sections read inlet → outlet → custody and
  // each meter's volume sits next to its own energy.
  const sections: { group: string; label: string; sources: SourceCatalogEntry[] }[] = [];
  for (const source of catalog?.sources ?? []) {
    const existing = sections.find((section) => section.group === source.group);
    if (existing) existing.sources.push(source);
    else sections.push({ group: source.group, label: source.group_label, sources: [source] });
  }

  return (
    <Stack gap="xs">
      <Group gap="xs" align="center" wrap="wrap">
        <Popover
          opened={opened}
          onChange={setOpened}
          position="bottom-start"
          width={360}
          shadow="md"
          withArrow
          trapFocus
        >
          <Popover.Target>
            <Button
              variant="light"
              leftSection={<IconPlus size={16} />}
              data-tour="data-source-picker"
              onClick={() => setOpened((o) => !o)}
              disabled={loading || !catalog}
              loading={loading}
            >
              Add sources
            </Button>
          </Popover.Target>
          <Popover.Dropdown p={0}>
            <ScrollArea.Autosize mah={420} type="hover">
              <Stack gap="xs" p="md">
                {sections.map((section, index) => (
                  <Stack key={section.group} gap={6}>
                    {index > 0 ? <Divider my={4} /> : null}
                    <Text size="xs" fw={600} c="dimmed" tt="uppercase" lts="0.06em">
                      {section.label}
                    </Text>
                    {section.sources[0]?.requires_facility ? (
                      <Accordion variant="contained" chevronPosition="left" multiple>
                        {section.sources.map((src) => {
                          const selectedCount = value.filter((v) => v.role === src.role).length;
                          return (
                            <Accordion.Item key={src.role} value={src.role}>
                              <Accordion.Control>
                                <Group gap={8} wrap="nowrap">
                                  <Text size="sm" fw={500}>
                                    {src.label}
                                  </Text>
                                  <Badge size="xs" variant="default">
                                    {unitLabel(src.unit)}
                                  </Badge>
                                  {selectedCount > 0 ? (
                                    <Badge size="xs" color="indigo">
                                      {selectedCount}
                                    </Badge>
                                  ) : null}
                                </Group>
                              </Accordion.Control>
                              <Accordion.Panel>
                                <Stack gap={6}>
                                  {dairyFacilities.map((f) => {
                                    const sel: Selection = {
                                      role: src.role,
                                      facility: f.identifier,
                                    };
                                    return (
                                      <Checkbox
                                        key={f.identifier}
                                        size="xs"
                                        label={f.display_name}
                                        checked={hasSelection(value, sel)}
                                        onChange={() => onChange(toggleSelection(value, sel))}
                                      />
                                    );
                                  })}
                                </Stack>
                              </Accordion.Panel>
                            </Accordion.Item>
                          );
                        })}
                      </Accordion>
                    ) : (
                      section.sources.map((src) => (
                        <SourceCheckbox
                          key={src.role}
                          source={src}
                          selection={{ role: src.role, facility: null }}
                          value={value}
                          onChange={onChange}
                        />
                      ))
                    )}
                  </Stack>
                ))}
              </Stack>
            </ScrollArea.Autosize>
          </Popover.Dropdown>
        </Popover>

        {value.length === 0 ? (
          <Text size="sm" c="dimmed">
            No sources selected
          </Text>
        ) : (
          <Button variant="subtle" color="gray" size="compact-sm" onClick={() => onChange([])}>
            Clear all
          </Button>
        )}
      </Group>

      {value.length > 0 ? (
        <Group gap={6} wrap="wrap">
          {value.map((sel) => (
            <Pill
              key={selectionKey(sel)}
              withRemoveButton
              onRemove={() => onChange(value.filter((x) => selectionKey(x) !== selectionKey(sel)))}
            >
              {selectionLabel(sel, catalog)}
            </Pill>
          ))}
        </Group>
      ) : null}
    </Stack>
  );
}

function SourceCheckbox({
  source,
  selection,
  value,
  onChange,
}: {
  source: SourceCatalogEntry;
  selection: Selection;
  value: Selection[];
  onChange: (next: Selection[]) => void;
}) {
  return (
    <Checkbox
      size="xs"
      checked={hasSelection(value, selection)}
      onChange={() => onChange(toggleSelection(value, selection))}
      label={
        <Group gap={6} wrap="nowrap">
          <Text size="sm">{source.label}</Text>
          <Badge size="xs" variant="default">
            {unitLabel(source.unit)}
          </Badge>
        </Group>
      }
      description={source.description}
    />
  );
}
